// Phase 13 — 4-in-1 Money Reports launcher (Payments / Claims / Sale Returns / Expenses)
// Phase 24C — CommonFilterBar adopted at the top for global Zone/SubZone scoping;
// individual cards read the shared filter values and pass through as params.
import { todayIST } from "@/lib/istDate";
import { useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CommonFilterBar from "@/components/reports/CommonFilterBar";
import { useReportsBackLink } from "@/components/reports/useReportsBackLink";
import PageHeader from "@/components/PageHeader";

const MODULES = [
  { key: "payments",    label: "Payments",    endpoint: "/reports/payments",     statuses: ["submitted", "verified", "discrepancy", "rejected"] },
  { key: "claims",      label: "Claims",      endpoint: "/reports/claims",       statuses: ["submitted", "asm_approved", "asm_rejected", "accounts_approved", "accounts_rejected", "paid"] },
  { key: "sale-returns",label: "Sale Returns",endpoint: "/reports/sale-returns", statuses: ["submitted", "asm_approved", "asm_rejected", "accounts_approved", "accounts_rejected"] },
  { key: "expenses",    label: "Expenses",    endpoint: "/reports/expenses",     statuses: ["submitted", "approved", "rejected"] },
];

function today() { return todayIST(); }
function nDaysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

async function downloadReport(endpoint, params, format, filename) {
  try {
    const qs = new URLSearchParams({ ...params, format }).toString();
    const res = await api.get(`${endpoint}/export?${qs}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
    toast.success(`${format.toUpperCase()} downloaded`);
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

function ModuleCard({ mod, extraParams = {} }) {
  const reportsBackTo = useReportsBackLink();
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState(todayIST());
  const [dateTo, setDateTo] = useState(today());
  const [count, setCount] = useState(null);

  const params = { date_from: dateFrom, date_to: dateTo,
                     ...(status ? { status } : {}),
                     ...(extraParams.zone_id ? { zone_id: extraParams.zone_id } : {}),
                     ...(extraParams.sub_zone_id ? { sub_zone_id: extraParams.sub_zone_id } : {}),
                     ...(extraParams.user_id ? { user_id: extraParams.user_id } : {}),
                     ...(extraParams.distributor_id ? { distributor_id: extraParams.distributor_id } : {}) };

  async function preview() {
    try {
      const qs = new URLSearchParams(params).toString();
      const { data } = await api.get(`${mod.endpoint}?${qs}&page_size=1`);
      setCount(data.total);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="border rounded-lg p-4 bg-white space-y-3" data-testid={`money-report-card-${mod.key}`}>
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: reportsBackTo }, { label: "Money" }]}
        title="Money reports"
        subtitle="Distributor payments, claims and expenses roll-up with export."
        favorite={{ key: "reports.money", type: "screen", label: "Money reports", route: "/reports/money" }}
        testid="moneyreports-page-header"
      />

      <h3 className="font-semibold text-base">{mod.label}</h3>
      <div className="flex flex-wrap gap-2">
        <div className="min-w-[110px] flex-1">
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-full" />
        </div>
        <div className="min-w-[110px] flex-1">
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-full" />
        </div>
        <div className="min-w-[110px] flex-1">
          <Label className="text-xs">Status</Label>
          <Select value={status || "__all__"} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any</SelectItem>
              {mod.statuses.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <Button size="sm" variant="outline" onClick={preview} data-testid={`money-report-preview-${mod.key}`}>Preview count</Button>
        {count !== null && <span className="text-xs text-neutral-500">→ {count} rows</span>}
        <div className="flex-1" />
        <Button size="sm" variant="outline" data-testid={`money-report-xlsx-${mod.key}`}
                onClick={() => downloadReport(mod.endpoint, params, "xlsx", `${mod.key}.xlsx`)}>
          <FileSpreadsheet className="w-3 h-3 mr-1" /> Excel
        </Button>
        <Button size="sm" data-testid={`money-report-pdf-${mod.key}`}
                onClick={() => downloadReport(mod.endpoint, params, "pdf", `${mod.key}.pdf`)}>
          <FileText className="w-3 h-3 mr-1" /> PDF
        </Button>
      </div>
    </div>
  );
}

export default function MoneyReports() {
  const [globalFilters, setGlobalFilters] = useState({ zone_id: "", sub_zone_id: "", user_id: "" });
  return (
    <div className="p-6 space-y-4" data-testid="money-reports-page">
      <div>
        <h1 className="text-2xl font-semibold">Money Reports</h1>
        <p className="text-sm text-neutral-500">All exports are branded (logo + colors) and honour role visibility.</p>
      </div>
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3">
          <CommonFilterBar value={globalFilters} onChange={setGlobalFilters}
                             showZone showSubZone showUser showDistributor />
        </CardContent>
      </Card>
      <div className="flex flex-wrap gap-4">
        {MODULES.map(m => (
          <div key={m.key} className="min-w-[280px] flex-1">
            <ModuleCard mod={m} extraParams={globalFilters} />
          </div>
        ))}
      </div>
    </div>
  );
}
